import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell, { BrandMark } from "@/components/AppShell";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search, Users2, Newspaper, Rocket, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import IndustryChips from "@/components/IndustryChips";
import CareerMoveCard from "@/components/CareerMoveCard";
import LeaderCard from "@/components/LeaderCard";

export default function Network() {
  const { user } = useAuth();
  const [industries, setIndustries] = useState([]);
  const [stats, setStats] = useState(null);
  const [industry, setIndustry] = useState("all");
  const [moves, setMoves] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const [ind, st] = await Promise.all([
        api.get("/network/industries"),
        api.get("/network/stats"),
      ]);
      setIndustries(ind.data.industries);
      setStats(st.data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = {};
      if (industry !== "all") params.industry = industry;
      const [m, l] = await Promise.all([
        api.get("/network/moves", { params: { ...params, limit: 30 } }),
        api.get("/network/leaders", { params: { ...params, q: q || undefined, limit: 60 } }),
      ]);
      setMoves(m.data);
      setLeaders(l.data);
      setLoading(false);
    })();
  }, [industry, q]);

  const industryLabelByKey = useMemo(() => {
    const map = {};
    industries.forEach((i) => { map[i.key] = i.label; });
    return map;
  }, [industries]);

  const chipItems = useMemo(() => {
    if (!stats) return industries.map((i) => ({ key: i.key, label: i.label }));
    const counts = Object.fromEntries((stats.by_industry || []).map((b) => [b.industry, b.leaders]));
    return industries.map((i) => ({ key: i.key, label: i.label, count: counts[i.key] || 0 }));
  }, [industries, stats]);

  const isAuthed = !!user && user !== false;

  const body = (
    <NetworkBody
      isAuthed={isAuthed}
      stats={stats}
      chipItems={chipItems}
      industry={industry}
      setIndustry={setIndustry}
      q={q}
      setQ={setQ}
      loading={loading}
      moves={moves}
      leaders={leaders}
      industryLabelByKey={industryLabelByKey}
    />
  );

  if (isAuthed) {
    return (
      <AppShell
        pageTitle="Healthcare Network"
        pageDescription="Career moves and senior leaders across India's healthcare industries."
      >
        {body}
      </AppShell>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Public header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/85 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/"><BrandMark /></Link>
          <div className="flex items-center gap-2">
            <Link to="/directory"><Button variant="ghost" className="hover:bg-primary/5">Directory</Button></Link>
            <Link to="/startups"><Button variant="ghost" className="hover:bg-primary/5" data-testid="nav-startups">Startups</Button></Link>
            <Link to="/login"><Button className="rounded-md" data-testid="nav-signin">Sign in</Button></Link>
          </div>
        </div>
      </header>

      {/* Public hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 lg:pt-14">
        <div className="max-w-3xl">
          <div className="overline">Network · India&apos;s healthcare people graph</div>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl text-primary leading-tight">
            Change Makers — <span className="italic text-accent">who moved where</span>.
          </h1>
          <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-primary/80" data-testid="change-makers-tag">
            <span className="w-1.5 h-1.5 rounded-full bg-accent" />
            Healthcare Leadership Tracker
          </div>
          <p className="mt-4 text-muted-foreground max-w-2xl">
            Track career transitions of CXOs, Medical Directors, Hospital Administrators, and senior doctors across
            Hospitals, Pharma, Diagnostics, MedTech, Biotech, Digital Health, Health AI, Insurance and beyond.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">{body}</div>
      <Footer />
    </div>
  );
}

function NetworkBody({ isAuthed, stats, chipItems, industry, setIndustry, q, setQ, loading, moves, leaders, industryLabelByKey }) {
  return (
    <div className={isAuthed ? "space-y-8" : "pt-8 space-y-8"}>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Leaders tracked" value={stats.total_leaders} icon={Users2} testid="network-stat-leaders" />
          <Stat label="Career moves" value={stats.total_moves} icon={Newspaper} testid="network-stat-moves" />
          <Stat label="Moves · 30 days" value={stats.recent_moves_30d} icon={ArrowRight} testid="network-stat-recent" />
          <Stat label="Startups mapped" value={stats.total_startups} icon={Rocket} testid="network-stat-startups" tone="primary" />
        </div>
      )}

      <IndustryChips items={chipItems} value={industry} onChange={setIndustry} />

      <Tabs defaultValue="moves">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <TabsList data-testid="network-tabs">
            <TabsTrigger value="moves" data-testid="tab-moves"><Newspaper size={14} className="mr-1.5" /> Recent moves</TabsTrigger>
            <TabsTrigger value="leaders" data-testid="tab-leaders"><Users2 size={14} className="mr-1.5" /> Leaders</TabsTrigger>
          </TabsList>
          <div className="relative w-full md:w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search by name, title or org…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="network-search-input"
            />
          </div>
        </div>

        <TabsContent value="moves" className="mt-6">
          {loading ? (
            <SkeletonList />
          ) : moves.length === 0 ? (
            <Empty label="No career moves recorded for this industry yet." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="moves-feed">
              {moves.map((m) => (
                <CareerMoveCard key={m.id} move={m} industryLabel={industryLabelByKey[m.to_industry]} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="leaders" className="mt-6">
          {loading ? (
            <SkeletonList />
          ) : leaders.length === 0 ? (
            <Empty label="No leaders match these filters." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="leaders-grid">
              {leaders.map((l) => <LeaderCard key={l.id} leader={l} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* CTA to startups */}
      <Link
        to="/startups"
        className="block rounded-xl border border-border bg-primary text-primary-foreground p-8 md:p-10 hover:opacity-95 transition-opacity"
        data-testid="cta-startups"
      >
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="overline text-primary-foreground/70">Directory</div>
            <h2 className="mt-2 font-serif text-3xl md:text-4xl leading-tight">Discover India&apos;s healthcare startups.</h2>
            <p className="mt-2 text-sm text-primary-foreground/80 max-w-xl">
              {stats?.total_startups || 0} founders and product lines — from digital health to medtech, biotech and health AI.
            </p>
          </div>
          <span className="rounded-md bg-white text-primary text-sm font-medium px-4 py-2.5 inline-flex items-center gap-1.5">
            Explore startup directory <ArrowRight size={14} />
          </span>
        </div>
      </Link>
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone, testid }) {
  return (
    <div className={`rounded-lg border border-border p-5 ${tone === "primary" ? "bg-primary text-primary-foreground" : "bg-white"}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className={`text-[10px] uppercase tracking-wider ${tone === "primary" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{label}</div>
        {Icon && <Icon size={14} className="opacity-70" />}
      </div>
      <div className="mt-2 font-serif text-3xl">{value ?? "—"}</div>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-36 rounded-lg border border-border bg-white animate-pulse" />)}
    </div>
  );
}

function Empty({ label }) {
  return <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">{label}</div>;
}
